import React, { useState, useEffect } from 'react';
import { TextGlitch } from './ui/text-glitch-effect';
import { motion } from 'framer-motion';

interface PublicationsIntroProps {
  onContinue?: () => void;
}

// Hook for detecting screen size
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

const PublicationsIntro: React.FC<PublicationsIntroProps> = ({ onContinue }) => {
  const { width, height } = useScreenSize();
  const isMobile = width < 640;
  const isSmallMobile = width < 380;
  const isVerySmallScreen = width <= 402 || height <= 874;

  // 根据屏幕尺寸调整动画参数
  const getTopAnimationProps = () => ({
    initial: { 
      x: isMobile ? 100 : 500, 
      rotate: isMobile ? 15 : 90
    },
    whileInView: { 
      x: 0, 
      rotate: 0
    },
    transition: { 
      duration: isMobile ? 0.5 : 1.2,
      ease: "easeOut" as const
    }
  });

  const getBottomAnimationProps = () => ({
    initial: { 
      x: isMobile ? -100 : -800
    },
    whileInView: { 
      x: 0
    },
    transition: { 
      duration: isMobile ? 0.5 : 1.2,
      delay: isMobile ? 0.1 : 0.3,
      ease: "easeOut" as const
    }
  });

  return (
    <section 
      id="publications"
      className="relative flex flex-col overflow-x-hidden" 
      style={{ 
        backgroundColor: '#2C2721', 
        minHeight: isVerySmallScreen 
          ? '100vh' 
          : isMobile 
            ? (isSmallMobile ? '50vh' : '55vh')
            : 'calc(100vh - 20px)',
        WebkitTouchCallout: 'none'
      }}
    >
      {/* Sometimes I do research - 响应式位置调整 */}
      <div className={`absolute z-10 ${
        isVerySmallScreen
          ? 'top-[25%] left-1 right-1'
          : isSmallMobile 
            ? 'top-[30%] left-1 right-1' 
            : isMobile
              ? 'top-[32%] left-2 right-2'
              : 'top-4 left-2 sm:top-6 sm:left-4 md:top-12 md:left-0 right-2 sm:right-4 md:right-auto'
      }`}>
        <motion.div
          {...getTopAnimationProps()}
          viewport={{ once: true, amount: 0.01 }}
          style={{ fontFamily: "'Gobold-Light', sans-serif" }}
        >
          <TextGlitch 
            text="sometimes i do research" 
            hoverText="publications" 
            delay={0} 
            className={`font-light border-b border-[#F1EFE0]/20 leading-tight text-[#F1EFE0] ${
              isVerySmallScreen
                ? 'text-[4.5vw]'
                : isSmallMobile 
                  ? 'text-[5vw]' 
                  : 'text-[6vw] sm:text-[5.5vw] md:text-[6.5vw] lg:text-[5.5vw] xl:text-[4.5vw]'
            }`}
          />
        </motion.div>
      </div>

      {/* Ideas → Papers with Icon - 响应式底部布局 */}
      <div className={`absolute left-2 right-2 ${
        isVerySmallScreen
          ? 'bottom-[20%]'
          : isSmallMobile 
            ? 'bottom-[25%]' 
            : isMobile
              ? 'bottom-[25%]'
              : 'bottom-6 sm:bottom-8 md:bottom-12 sm:left-4 sm:right-4 md:left-0 md:right-0'
      }`}>
        <div className={`flex flex-col items-center relative ${
          isSmallMobile ? 'px-1' : 'px-2 sm:px-4'
        }`}>
          {/* SVG Icon - 响应式位置和大小 */}
          <motion.div
            initial={{ 
              scale: 0.5,
              rotate: isMobile ? 30 : 180
            }}
            whileInView={{ 
              scale: 1,
              rotate: 0
            }}
            transition={{ 
              duration: isMobile ? 0.4 : 0.8,
              delay: 0.05,
              ease: "easeOut" as const
            }}
            viewport={{ once: true, amount: 0.01 }}
            className={`absolute z-20 ${
              isVerySmallScreen
                ? 'mb-1 right-1 -top-1'
                : isSmallMobile 
                  ? 'mb-1 right-1 -top-2' 
                  : isMobile
                    ? 'mb-2 right-2 -top-3'
                    : 'mb-2 sm:mb-4 right-2 sm:right-4 md:right-4 -top-6 sm:-top-8 md:-top-10 lg:-top-14 xl:-top-20'
            }`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 512 512" 
              className={
                isVerySmallScreen
                  ? 'w-6 h-6'
                  : isSmallMobile 
                    ? 'w-8 h-8' 
                    : 'w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24'
              }
            >
              <rect width="100%" height="100%" fill="none"/>
              <circle 
                cx="256" 
                cy="256" 
                r="196" 
                stroke="#F1EFE0" 
                strokeWidth={isSmallMobile ? "28" : "32"} 
                fill="none" 
                className={!isSmallMobile ? "sm:stroke-[40] md:stroke-[48]" : ""}
              />
              <text 
                x="256" 
                y="256" 
                textAnchor="middle" 
                dominantBaseline="middle"
                fontFamily="Arial, Helvetica, sans-serif" 
                fontWeight="700" 
                fontSize={isSmallMobile ? "180" : "200"}
                fill="#F1EFE0"
                className={!isSmallMobile ? "sm:text-[220px] md:text-[260px]" : ""}
              >
                K
              </text>
            </svg>
          </motion.div>

          {/* Text - 响应式文字 */}
          <motion.div
            {...getBottomAnimationProps()}
            viewport={{ once: true, amount: 0.01 }}
            className="w-full max-w-full overflow-hidden relative z-30"
            style={{ 
              fontFamily: "'Gobold-Bold', sans-serif",
              textShadow: isMobile 
                ? '1px 1px 0px rgba(241, 239, 224, 0.6), -1px -1px 0px rgba(241, 239, 224, 0.6)' 
                : '1px 1px 0px rgba(241, 239, 224, 0.8), -1px -1px 0px rgba(241, 239, 224, 0.8), 1px -1px 0px rgba(241, 239, 224, 0.8), -1px 1px 0px rgba(241, 239, 224, 0.8)'
            }}
          >
            <div 
              className={`cursor-pointer transition-all duration-300 ${
                isMobile 
                  ? 'active:scale-95' 
                  : 'hover:scale-105'
              }`}
              style={{
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <TextGlitch 
                text="ideas → paper" 
                hoverText="explore" 
                onClick={onContinue}
                delay={0.2} 
                className={`text-center font-black leading-none text-[#F1EFE0] ${
                  isVerySmallScreen
                    ? 'text-[7.5vw]'
                    : isSmallMobile 
                      ? 'text-[9vw]' 
                      : 'text-[11vw] sm:text-[12vw] md:text-[14vw] lg:text-[16vw] xl:text-[14vw]'
                }`}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PublicationsIntro;